import { useMutation } from '@apollo/client';
import { Loader2Icon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { toast } from 'sonner';

import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Input,
  ModalContent,
  ModalFooter,
  RadioCheckboxItem,
  RadioGroup,
  useErrorHandler,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  CreateOrUpdateProofOfCompletionDocument,
  GetViewerLearningDetailsDocument,
  ProofOfCompletionType,
  ExamStatementType,
} from '@sandtech-hub-monorepo/ehub-client';
import { useGetMaybeViewer } from '@sandtech-hub-monorepo/ui-auth-kit';

import { ReactComponent as CertificateIcon } from '../../assets/Icons/certificate.svg';
import { zodResolver } from '@hookform/resolvers/zod';
import { ProofOfCompletionModalProps } from './Banner';
import {
  ProofOfCompletionGenericFormFields,
  proofOfCompletionGenericFormSchema,
} from './GenericFormFields';
import {
  getExamLocationOptions,
  getLabelByExamStatement,
  getModalSubtitleByProofOfCompletionType,
} from './util';

const formSchema = proofOfCompletionGenericFormSchema.extend({
  trailblazerProfileLink: z.string().min(1),
});

export const SalesforceAdministratorModal = ({
  classEnrollmentId,
  close,
}: ProofOfCompletionModalProps) => {
  const { viewer } = useGetMaybeViewer();
  const [createProofOfCompletion, { loading }] = useMutation(
    CreateOrUpdateProofOfCompletionDocument
  );

  const { handleGeneralAppError } = useErrorHandler();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: viewer?.firstName || '',
      lastName: viewer?.lastName || '',
      emailUsedInExam: viewer?.primaryEmail || '',
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    createProofOfCompletion({
      variables: {
        input: {
          classEnrollmentId,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.emailUsedInExam,
          trailblazerProfileLink: values.trailblazerProfileLink,
          examLocation: values.examLocation,
          examStatement: values.examStatement,
        },
      },
      refetchQueries: [{ query: GetViewerLearningDetailsDocument }],
      onError: () => handleGeneralAppError(),
      onCompleted: () => {
        toast.info('Your Proof of completion was successfully submitted!');
        close();
      },
    });
  };

  return (
    <ModalContent>
      <div className="flex flex-col gap-8 w-[80%] mx-auto">
        <div className="flex flex-col gap-4 items-center justify-center mx-auto">
          <div className="flex items-center justify-center w-24 h-24 bg-background-quaternary rounded-full">
            <CertificateIcon />
          </div>
          <h2 className="font-semibold text-2xl text-primary">
            Proof of Completion
          </h2>
          <p className="font-medium text-center">
            {getModalSubtitleByProofOfCompletionType(
              ProofOfCompletionType.SalesforceAdministrator
            )}
          </p>
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-5">
              <ProofOfCompletionGenericFormFields
                proofOfCompletionType={
                  ProofOfCompletionType.SalesforceAdministrator
                }
              />

              <FormField
                control={form.control}
                name="trailblazerProfileLink"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel
                      htmlFor="trailblazerProfileLink"
                      className="text-sm"
                    >
                      Kindly share your Trailblazer profile link and ensure that
                      your profile is public.{' '}
                      <a
                        href="https://drive.google.com/file/d/1ZmnFOLcNJNY5iSXTp3lR-cm1WF5dTOy0/view?usp=sharing"
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary"
                      >
                        Here
                      </a>{' '}
                      is a guide on how to do so
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="examLocation"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Where did you take your exam?*</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <div className="flex flex-col gap-4">
                          {getExamLocationOptions(
                            ProofOfCompletionType.SalesforceAdministrator
                          ).map((option) => (
                            <RadioCheckboxItem
                              id={option.value}
                              value={option.value}
                              label={option.label}
                            />
                          ))}
                        </div>
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="examStatement"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>
                      Which of these statements applies to you with regard to
                      the Salesforce Administrator Exam?*
                    </FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <div className="flex flex-col gap-4">
                          {Object.values(ExamStatementType).map(
                            (statementType) => (
                              <RadioCheckboxItem
                                id={statementType}
                                value={statementType}
                                label={getLabelByExamStatement(
                                  statementType,
                                  ProofOfCompletionType.SalesforceAdministrator
                                )}
                              />
                            )
                          )}
                        </div>
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <ModalFooter className="sm:justify-center">
              <Button variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!form.formState.isDirty || loading}
              >
                {loading ? <Loader2Icon className="animate-spin" /> : 'Submit'}
              </Button>
            </ModalFooter>
          </form>
        </Form>
      </div>
    </ModalContent>
  );
};
